import { z } from "zod";
import { IdPageQuery, IsoDateTime, Uuid, pageOf } from "./common";
import { CustomerRole } from "./customers";

export const TRACKING_DESTINATIONS = ["meta", "google_ads", "ga4", "tiktok"] as const;
export const TrackingDestination = z.enum(TRACKING_DESTINATIONS);
export type TrackingDestination = z.infer<typeof TrackingDestination>;

export const SEND_STATUSES = ["pending", "sending", "sent", "dry_run", "skipped", "failed"] as const;
export const SendStatus = z.enum(SEND_STATUSES);
export type SendStatus = z.infer<typeof SendStatus>;

/** Why a destination did not get a conversion. Stable codes the UI translates. */
export const SEND_REASONS = [
  "no_consent",
  "consent_unproven",
  "consent_withdrawn",
  "no_ad_user_data",
  "not_configured",
  "no_conversion_action",
  "no_click_id",
  "no_match_data",
  "no_client_id",
  "too_old",
  "regulated_not_generic",
  "live_off",
  "rejected",
  "auth_failed",
  "retries_exhausted",
  "ambiguous_not_resent",
] as const;
export const SendReason = z.enum(SEND_REASONS);
export type SendReason = z.infer<typeof SendReason>;

export const CONVERSION_SOURCES = ["website", "app", "phone", "store", "reception", "other"] as const;
export const ConversionSource = z.enum(CONVERSION_SOURCES);
export type ConversionSource = z.infer<typeof ConversionSource>;

export const CONSENT_STATES = ["granted", "denied", "unknown", "unproven", "withdrawn"] as const;
export const ConsentState = z.enum(CONSENT_STATES);
export type ConsentState = z.infer<typeof ConsentState>;

export const CONSENT_ORIGINS = ["event", "visitor", "contact", "none"] as const;
export const ConsentOrigin = z.enum(CONSENT_ORIGINS);

export const CLICK_ID_KINDS = ["fbclid", "gclid", "gbraid", "wbraid", "ttclid", "li_fat_id"] as const;
export type ClickIdKind = (typeof CLICK_ID_KINDS)[number];

export const ATTRIBUTION_CHANNELS = ["meta", "google", "tiktok", "linkedin", "utm"] as const;
export const AttributionChannel = z.enum(ATTRIBUTION_CHANNELS);
export type AttributionChannel = z.infer<typeof AttributionChannel>;

const Hostname = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^(\*\.)?[a-z0-9]([a-z0-9-]*[a-z0-9])?(\.[a-z0-9]([a-z0-9-]*[a-z0-9])?)*$/, "hostname");
const Sha256 = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-f0-9]{64}$/, "sha256 hex");
const Currency = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{3}$/, "ISO currency");
const ClickId = z.string().trim().min(1).max(512).regex(/^\S+$/);
const ShortText = z.string().trim().min(1).max(300);
const Slug = z.string().trim().min(1).max(80).regex(/^[a-z][a-z0-9_.-]*$/);
const EventId = z.string().trim().min(1).max(200).regex(/^[\x21-\x7e]+$/, "printable ascii");
const VisitorId = z.string().trim().min(8).max(64).regex(/^[A-Za-z0-9_-]+$/);
const GaClientId = z.string().trim().max(64).regex(/^[0-9]+\.[0-9]+$/, "client id");
const MetaPixelId = z.string().trim().regex(/^\d{3,32}$/, "pixel id");
const Ga4MeasurementId = z.string().trim().regex(/^G-[A-Z0-9]{4,20}$/, "measurement id");
const GoogleAdsTagId = z.string().trim().regex(/^AW-\d{4,20}$/, "conversion id");
const GoogleCustomerId = z
  .string()
  .trim()
  .transform((v) => v.replace(/-/g, ""))
  .pipe(z.string().regex(/^\d{6,12}$/, "customer id"));
const TiktokPixelId = z.string().trim().regex(/^[A-Z0-9]{6,40}$/, "pixel id");
const Secret = z.string().trim().min(8).max(4096);

export const UtmParams = z.strictObject({
  source: ShortText.optional(),
  medium: ShortText.optional(),
  campaign: ShortText.optional(),
  term: ShortText.optional(),
  content: ShortText.optional(),
  id: ShortText.optional(),
  /** Imagora's own parameter, `utm_topic`: which topic a link came from. */
  topic: ShortText.optional(),
});
export type UtmParams = z.infer<typeof UtmParams>;

export const ClickIds = z.strictObject({
  fbclid: ClickId.optional(),
  gclid: ClickId.optional(),
  gbraid: ClickId.optional(),
  wbraid: ClickId.optional(),
  ttclid: ClickId.optional(),
  li_fat_id: ClickId.optional(),
});
export type ClickIds = z.infer<typeof ClickIds>;

export const GoogleConversionAction = z.strictObject({
  /** The conversion action of type UPLOAD_CLICKS, for Data Manager. */
  actionId: z.string().trim().regex(/^\d{1,20}$/).optional(),
  /** The label of the browser tag's conversion. */
  label: z.string().trim().regex(/^[A-Za-z0-9_-]{1,64}$/).optional(),
});
export type GoogleConversionAction = z.infer<typeof GoogleConversionAction>;

/** The tenant's configuration as the API shows it: secrets only as "is set". */
export const TrackingConfigDto = z.object({
  tenantId: Uuid,
  publicKey: z.string(),
  metaPixelId: z.string().nullable(),
  metaTestEventCode: z.string().nullable(),
  hasMetaCapiToken: z.boolean(),
  ga4MeasurementId: z.string().nullable(),
  hasGa4ApiSecret: z.boolean(),
  googleAdsTagId: z.string().nullable(),
  googleAdsCustomerId: z.string().nullable(),
  googleAdsLoginCustomerId: z.string().nullable(),
  hasGoogleRefreshToken: z.boolean(),
  googleConversionActions: z.record(z.string(), GoogleConversionAction),
  tiktokPixelId: z.string().nullable(),
  hasTiktokAccessToken: z.boolean(),
  allowedDomains: z.array(z.string()),
  bannerEnabled: z.boolean(),
  consentTextVersion: z.string(),
  regulated: z.boolean(),
  regulatedBrowserTags: z.boolean(),
  /** The one line a host puts in its templates. */
  snippet: z.string(),
  collectorUrl: z.string(),
  updatedAt: IsoDateTime,
});
export type TrackingConfigDto = z.infer<typeof TrackingConfigDto>;

/** Null clears a field. A secret is write-only: the API never gives it back. */
export const TrackingConfigPatch = z.strictObject({
  metaPixelId: MetaPixelId.nullable().optional(),
  metaCapiToken: Secret.nullable().optional(),
  metaTestEventCode: z.string().trim().regex(/^[A-Za-z0-9_-]{1,64}$/).nullable().optional(),
  ga4MeasurementId: Ga4MeasurementId.nullable().optional(),
  ga4ApiSecret: Secret.nullable().optional(),
  googleAdsTagId: GoogleAdsTagId.nullable().optional(),
  googleAdsCustomerId: GoogleCustomerId.nullable().optional(),
  googleAdsLoginCustomerId: GoogleCustomerId.nullable().optional(),
  googleRefreshToken: Secret.nullable().optional(),
  googleConversionActions: z.record(Slug, GoogleConversionAction).optional(),
  tiktokPixelId: TiktokPixelId.nullable().optional(),
  tiktokAccessToken: Secret.nullable().optional(),
  allowedDomains: z.array(Hostname).max(50).optional(),
  bannerEnabled: z.boolean().optional(),
  consentTextVersion: z.string().trim().min(1).max(40).optional(),
  regulatedBrowserTags: z.boolean().optional(),
});
export type TrackingConfigPatch = z.infer<typeof TrackingConfigPatch>;

export const BannerText = z.object({
  title: z.string(),
  body: z.string(),
  accept: z.string(),
  refuse: z.string(),
});
export type BannerText = z.infer<typeof BannerText>;

/** What a public page may read by key. Ids only. */
export const PublicTrackingConfig = z.object({
  textVersion: z.string(),
  banner: BannerText.nullable(),
  regulated: z.boolean(),
  /** On a regulated vertical, the only event names that may reach a third-party tag. Empty otherwise. */
  genericEvents: z.array(z.string()),
  tags: z.object({
    metaPixelId: z.string().nullable(),
    ga4MeasurementId: z.string().nullable(),
    googleAdsTagId: z.string().nullable(),
    /** Browser event name to Google Ads label. */
    googleAdsLabels: z.record(z.string(), z.string()),
    tiktokPixelId: z.string().nullable(),
  }),
});
export type PublicTrackingConfig = z.infer<typeof PublicTrackingConfig>;

export const PublicConfigQuery = z.object({ lang: z.string().trim().min(2).max(35).optional() });

/** The snippet's beacon, as a compact body so a page's every view costs little. Anything else is refused. */
export const CollectorPayload = z.strictObject({
  /** The tenant's public key. */
  k: z.string().trim().min(8).max(80),
  /** event | consent */
  ty: z.enum(["event", "consent"]),
  /** Decided at or happened at, ms since the epoch, as the browser saw it. */
  ts: z.number().int().positive(),
  c: z.strictObject({ m: z.boolean(), a: z.boolean(), tv: z.string().trim().max(40).optional(), s: z.enum(["banner", "host"]).optional() }),
  n: Slug.optional(),
  id: EventId.optional(),
  v: VisitorId.optional(),
  url: z.string().trim().max(2000).optional(),
  ref: z.string().trim().max(2000).optional(),
  ck: ClickIds.optional(),
  ckt: z.number().int().positive().optional(),
  utm: UtmParams.optional(),
  val: z.number().int().min(0).max(2_000_000_000).optional(),
  cur: Currency.optional(),
  ga: GaClientId.optional(),
  fbp: z.string().trim().max(200).optional(),
  fbc: z.string().trim().max(600).optional(),
  ttp: z.string().trim().max(200).optional(),
});
export type CollectorPayload = z.infer<typeof CollectorPayload>;

export const EventConsent = z.strictObject({
  marketing: z.boolean(),
  /** Consent Mode's two signals for Google. Left out, they follow `marketing`. */
  adUserData: z.boolean().optional(),
  adPersonalization: z.boolean().optional(),
  /** The proof: a grant without when and which wording is recorded and not sent. */
  textVersion: z.string().trim().min(1).max(100).optional(),
  at: IsoDateTime.optional(),
  /** The host's own record of the grant. */
  proofRef: z.string().trim().min(1).max(200).optional(),
});

export const EventUser = z.strictObject({
  /** Hashed per destination on arrival; the plain value is never stored. */
  email: z.string().trim().min(3).max(320).optional(),
  phone: z.string().trim().min(5).max(40).optional(),
  /** SHA-256 of the address trimmed and lowercased. Good for Meta and TikTok; for Google too, except a Gmail address. */
  emailSha256: Sha256.optional(),
  /** SHA-256 of the digits with the country code and no plus. Meta's form: Google and TikTok need the next field. */
  phoneSha256: Sha256.optional(),
  /** SHA-256 of the address with Gmail's dots and plus tag dropped. */
  googleEmailSha256: Sha256.optional(),
  /** SHA-256 of the number in E.164, with its plus. */
  e164PhoneSha256: Sha256.optional(),
  /** The host's own customer id; hashed on arrival. */
  externalId: z.string().trim().min(1).max(200).optional(),
  /** Anchors a national phone number. Falls back to the tenant's country. */
  country: z.string().trim().length(2).optional(),
  /** What the person is to the tenant. A medical tenant takes an event about a person only with `provider`, or one already on file as that. */
  role: CustomerRole.optional(),
});

/** `POST /v1/tenants/{id}/events`. `eventId` is the dedup key and the id shared with the browser. */
export const HostEventInput = z
  .strictObject({
    type: Slug,
    eventId: EventId,
    /** Google's dedup key. Defaults to `eventId`. */
    transactionId: EventId.optional(),
    occurredAt: IsoDateTime.optional(),
    source: ConversionSource.optional(),
    valueMinor: z.number().int().min(0).max(2_000_000_000).optional(),
    currency: Currency.optional(),
    consent: EventConsent.optional(),
    visitorId: VisitorId.optional(),
    gaClientId: GaClientId.optional(),
    fbp: z.string().trim().max(200).optional(),
    fbc: z.string().trim().max(600).optional(),
    ttp: z.string().trim().max(200).optional(),
    clicks: ClickIds.optional(),
    clickedAt: IsoDateTime.optional(),
    utm: UtmParams.optional(),
    user: EventUser.optional(),
    context: z
      .strictObject({
        ip: z.string().trim().max(64).optional(),
        userAgent: z.string().trim().max(512).optional(),
        url: z.string().trim().max(2000).optional(),
      })
      .optional(),
    attribution: z
      .strictObject({
        campaignRef: ShortText.optional(),
        adSetRef: ShortText.optional(),
        creativeRef: ShortText.optional(),
        topicRef: ShortText.optional(),
      })
      .optional(),
    contents: z.array(z.strictObject({ id: ShortText, quantity: z.number().int().min(1).max(100000).optional(), priceMinor: z.number().int().min(0).optional() })).max(100).optional(),
    data: z.record(z.string(), z.unknown()).optional(),
  })
  .refine((event) => event.valueMinor === undefined || event.currency !== undefined, { path: ["currency"], message: "currency is required with valueMinor" });
export type HostEventInput = z.infer<typeof HostEventInput>;

export const ConversionSendDto = z.object({
  destination: TrackingDestination,
  status: SendStatus,
  reason: SendReason.nullable(),
  attempts: z.number().int(),
  nextAttemptAt: IsoDateTime.nullable(),
  lastError: z.string().nullable(),
  remoteRef: z.string().nullable(),
  sentAt: IsoDateTime.nullable(),
});
export type ConversionSendDto = z.infer<typeof ConversionSendDto>;

export const ConversionDto = z.object({
  id: Uuid,
  eventId: z.string(),
  transactionId: z.string(),
  type: z.string(),
  category: z.enum(["conversion", "signal"]),
  source: ConversionSource,
  valueMinor: z.number().int().nullable(),
  currency: z.string().nullable(),
  occurredAt: IsoDateTime,
  recordedAt: IsoDateTime,
  consentState: ConsentState,
  consentOrigin: ConsentOrigin,
  /** True when no destination received or will receive it: the plan's "not attributed externally". */
  internalOnly: z.boolean(),
  attribution: z.object({
    channel: AttributionChannel.nullable(),
    clickedAt: IsoDateTime.nullable(),
    utmSource: z.string().nullable(),
    utmMedium: z.string().nullable(),
    utmCampaign: z.string().nullable(),
    campaignRef: z.string().nullable(),
    adSetRef: z.string().nullable(),
    creativeRef: z.string().nullable(),
    topicRef: z.string().nullable(),
  }),
  matchedBy: z.enum(["email", "phone"]).nullable(),
  sends: z.array(ConversionSendDto),
});
export type ConversionDto = z.infer<typeof ConversionDto>;

export const EventAccepted = z.object({
  /** True when this event id was already recorded: `conversion` is the first record, and nothing was sent again. */
  duplicate: z.boolean(),
  conversion: ConversionDto,
});
export type EventAccepted = z.infer<typeof EventAccepted>;

const DateRange = {
  from: IsoDateTime.optional(),
  to: IsoDateTime.optional(),
};

export const ConversionListQuery = IdPageQuery.extend({
  ...DateRange,
  source: ConversionSource.optional(),
  type: Slug.optional(),
  destination: TrackingDestination.optional(),
  status: SendStatus.optional(),
  reason: SendReason.optional(),
  campaign: ShortText.optional(),
});
export type ConversionListQuery = z.infer<typeof ConversionListQuery>;
export const ConversionPage = pageOf(ConversionDto);
export type ConversionPage = z.infer<typeof ConversionPage>;

export const RangeQuery = z.object(DateRange);
export type RangeQuery = z.infer<typeof RangeQuery>;

export const ConversionReconcileDto = z.object({
  weekStart: z.string(),
  destination: TrackingDestination,
  ourCount: z.number().int(),
  platformCount: z.number().int().nullable(),
  status: z.enum(["compared", "unavailable"]),
  flagged: z.boolean(),
  detail: z.string().nullable(),
});
export type ConversionReconcileDto = z.infer<typeof ConversionReconcileDto>;

export const ConversionSummary = z.object({
  from: IsoDateTime.nullable(),
  to: IsoDateTime.nullable(),
  total: z.number().int(),
  internalOnly: z.number().int(),
  bySource: z.array(z.object({ source: ConversionSource, count: z.number().int() })),
  valueByCurrency: z.array(z.object({ currency: z.string(), valueMinor: z.number().int(), count: z.number().int() })),
  byDestination: z.array(
    z.object({
      destination: TrackingDestination,
      status: SendStatus,
      reason: SendReason.nullable(),
      count: z.number().int(),
    }),
  ),
  /** Weeks where our count and the platform's differ by more than the threshold. */
  gaps: z.array(ConversionReconcileDto),
});
export type ConversionSummary = z.infer<typeof ConversionSummary>;

export const ConversionReconcileList = z.object({ items: z.array(ConversionReconcileDto) });

export const TrackingFunnel = z.object({
  from: IsoDateTime.nullable(),
  to: IsoDateTime.nullable(),
  steps: z.array(z.object({ name: z.string(), events: z.number().int() })),
  conversions: z.number().int(),
});
export type TrackingFunnel = z.infer<typeof TrackingFunnel>;

export const ConsentSummary = z.object({
  from: IsoDateTime.nullable(),
  to: IsoDateTime.nullable(),
  decisions: z.array(z.object({ marketing: z.boolean(), analytics: z.boolean(), count: z.number().int() })),
  conversionsByState: z.array(z.object({ state: ConsentState, count: z.number().int() })),
});
export type ConsentSummary = z.infer<typeof ConsentSummary>;

export const TestEventResult = z.object({
  results: z.array(
    z.object({
      destination: TrackingDestination,
      status: z.enum(["sent", "dry_run", "skipped", "failed"]),
      reason: SendReason.nullable(),
      error: z.string().nullable(),
    }),
  ),
});
export type TestEventResult = z.infer<typeof TestEventResult>;
